import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { ValueLabel } from 'src/models';
import { changedKeys } from 'src/utils/common';

import { initialState } from './const';
import { setDashboardData } from './helper';
import type { Dashboard, FieldOperationsQuery } from './model';
import {
  getDashboardData,
  getDashboardSettings,
  getListQuestions,
  getSurveyNames,
  getTagConfig,
  getTags,
} from './thunk';

const dashboardReducer = createSlice({
  name: '@dashboard',
  initialState,
  reducers: {
    hideDashboardLoading: state => {
      state.countLoading--;
    },
    setDashboardFilterParams: (state, action: PayloadAction<Dashboard['dashboardFilterParams']>) => {
      const keys = changedKeys(state.dashboardFilterParams, action.payload);
      state.dashboardFilterParams = {
        ...action.payload,
        contact_bar: action.payload.contact_bar === 'Call Text' ? 'call_text' : action.payload.contact_bar,
      };
      state.changedKeys = keys;
    },
    updateChartFilterParams: (state, action: PayloadAction<Dashboard['dashboardFilterParams']>) => {
      state.dashboardFilterParams = action.payload;
    },
    setFilterParam: (state, action: PayloadAction<Dashboard['filterParam']>) => {
      state.filterParam = action.payload;
    },
    setIsFilterChartsByChart: (state, action: PayloadAction<Dashboard['isFilterChartsByChart']>) => {
      state.isFilterChartsByChart = action.payload;
    },
    setIsClearFilter: (state, action: PayloadAction<Dashboard['isClearFilter']>) => {
      state.isClearFilter = action.payload;
    },
    setIsApplyRemoveFilter: (state, action: PayloadAction<Dashboard['isApplyRemoveFilter']>) => {
      state.isApplyRemoveFilter = action.payload;
    },
    setIsExportData: (state, action: PayloadAction<Dashboard['isExportData']>) => {
      state.isExportData = action.payload;
    },
    setIsExportDataClicked: (state, action: PayloadAction<Dashboard['isExportDataClicked']>) => {
      state.isExportDataClicked = action.payload;
    },
    setIsImportData: (state, action: PayloadAction<Dashboard['isImportData']>) => {
      state.isImportData = action.payload;
    },
    setIsImportDataClicked: (state, action: PayloadAction<Dashboard['isImportDataClicked']>) => {
      state.isImportDataClicked = action.payload;
    },
    setCityCouncilColors: (state, action: PayloadAction<Dashboard['cityCouncilColors']>) => {
      state.cityCouncilColors = action.payload;
    },
    setIsCreateBlockwalksWarning: (state, action: PayloadAction<Dashboard['isCreateBlockwalksWarning']>) => {
      state.isCreateBlockwalksWarning = action.payload;
    },
    setIsVisibleCharts: (state, action: PayloadAction<Dashboard['isVisibleCharts']>) => {
      state.isVisibleCharts = action.payload;
    },
    setIsSwitchCandidate: (state, action: PayloadAction<Dashboard['isSwitchCandidate']>) => {
      state.isSwitchCandidate = action.payload;
    },
    setIsLoadingChart: (state, action: PayloadAction<Dashboard['isLoadingChart']>) => {
      state.isLoadingChart = action.payload;
    },
    setIsLoadingDefault: (state, action: PayloadAction<Dashboard['isLoadingDefault']>) => {
      state.isLoadingDefault = action.payload;
    },
    // =========== setSyncingData ================
    setSyncingData: (state, action: PayloadAction<Dashboard['syncingData']>) => {
      state.syncingData = action.payload;
    },
    setImportContact: (state, action: PayloadAction<Dashboard['importContact']>) => {
      state.importContact = action.payload;
    },
    setTagAndOr: (state, action: PayloadAction<Dashboard['tagAndOr']>) => {
      state.tagAndOr = action.payload;
    },
    setSurveyAndOr: (state, action: PayloadAction<Dashboard['surveyAndOr']>) => {
      state.surveyAndOr = action.payload;
    },
    setFieldOperations: (state, action: PayloadAction<Partial<FieldOperationsQuery>>) => {
      state.fieldOperationsQuery = {
        ...state.fieldOperationsQuery,
        ...action.payload,
      };
    },
    setBlocks: (state, action: PayloadAction<Dashboard['campaignBlocks']>) => {
      state.campaignBlocks = action.payload;
    },
    fieldOperationsSelected: (state, action: PayloadAction<ValueLabel<string, string> | null>) => {
      if (state.fieldOperationsSelected?.value === action.payload?.value) {
        state.fieldOperationsSelected = null;
      } else {
        state.fieldOperationsSelected = action.payload;
      }
    },
    assignedToField: (state, action: PayloadAction<Dashboard['assignedToField']>) => {
      state.assignedToField = action.payload;
    },
    setActivityQuery: (state, action: PayloadAction<Dashboard['activityQuery']>) => {
      state.activityQuery = {
        ...state.activityQuery,
        ...action.payload,
      };
    },
    setListActivity: (state, action: PayloadAction<Dashboard['listActivity']>) => {
      if (state.activityQuery?.page === 1) {
        state.listActivity = action.payload;
      } else {
        state.listActivity = [...state.listActivity, ...action.payload];
      }
    },
    setSelectedSurvey: (state, action: PayloadAction<string[]>) => {
      state.selectedSurvey = action.payload;
    },
    removeSurveyQuestions: (state, action: PayloadAction<string>) => {
      delete state.questionsBySurvey[action.payload];
    },
    setLalVoterIds: (state, action: PayloadAction<string[]>) => {
      state.lalVoterIds = action.payload;
    },
    resetState: (_, action: PayloadAction<Partial<Dashboard> | undefined>) => {
      return {
        ...initialState,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    //=========== getDashboardSettings ================
    builder.addCase(getDashboardSettings.pending, state => {
      if (state.firstLoad) {
        state.countLoading++;
      }
    });
    builder.addCase(getDashboardSettings.fulfilled, (state, action) => {
      state.dashboardSettings = action.payload || null;
    });
    //=========== getDashboardData ================
    builder.addCase(getDashboardData.pending, state => {
      if (state.firstLoad) {
        state.countLoading++;
      }
    });
    builder.addCase(getDashboardData.fulfilled, setDashboardData);
    // =========== Tags Config ================
    builder.addCase(getTagConfig.fulfilled, (state, action) => {
      state.tagConfig = action.payload;
    });
    // =========== Tags ================
    builder.addCase(getTags.fulfilled, (state, action) => {
      state.tags = action.payload;
    });
    // =========== List Questions ================
    builder.addCase(getListQuestions.fulfilled, (state, action) => {
      action.payload.forEach(survey => {
        state.questionsBySurvey[survey.id] = survey.questions;
      });

      state.questions = Object.values(state.questionsBySurvey).flat();

      state.loadingQuestions = false;
    });
    builder.addCase(getListQuestions.pending, state => {
      state.loadingQuestions = true;
    });
    builder.addCase(getListQuestions.rejected, state => {
      state.loadingQuestions = false;
    });
    // =========== Survey Names ================
    builder.addCase(getSurveyNames.fulfilled, (state, action) => {
      state.surveyNames = action.payload;

      state.loadingSurveyNames = false;
    });
    builder.addCase(getSurveyNames.pending, state => {
      state.loadingSurveyNames = true;
    });
    builder.addCase(getSurveyNames.rejected, state => {
      state.loadingSurveyNames = false;
    });
  },
});

export const dashboardSliceActions = dashboardReducer.actions;
export default dashboardReducer.reducer;
